// Local settings storage using localStorage
import type { Settings } from '@/types'

const SETTINGS_KEY = 'ottotester-settings'
/** The key older versions saved under. Read only as a fallback, so a
 * user's settings carry over, and removed once a save to the new key
 * succeeds. */
const LEGACY_SETTINGS_KEY = 'asta-gen-settings'

const DEFAULT_SETTINGS: Settings = {
  openai: {
    model: 'gpt-4o',
    temperature: 0.7,
  },
  browser: {
    type: 'chromium',
    headless: true,
    // viewportWidth/viewportHeight intentionally omitted — backend default
    // (1920x1080) lives in server/src/services/playwrightConfigGenerator.ts.
    timeout: 30000,
    screenshotOnFailure: true,
    recordVideo: false,
    slowMotion: false,
  },
  theme: 'system',
}

export const settingsStorage = {
  get(): Settings {
    // The new key wins when it reads cleanly; the old key is the fallback.
    return readKey(SETTINGS_KEY) ?? readKey(LEGACY_SETTINGS_KEY) ?? DEFAULT_SETTINGS
  },

  save(settings: Settings): void {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    // Only reached when the save above did not throw, so the old key is
    // never dropped before its settings live under the new one.
    localStorage.removeItem(LEGACY_SETTINGS_KEY)
  },

  reset(): void {
    localStorage.removeItem(SETTINGS_KEY)
    localStorage.removeItem(LEGACY_SETTINGS_KEY)
  },
}

/** Saved settings under `key`, merged over the defaults, or null when the
 * key is missing or does not parse. */
function readKey(key: string): Settings | null {
  try {
    const stored = localStorage.getItem(key)
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
    }
  } catch {
    // Unreadable; treat as missing.
  }
  return null
}
